import { memo } from 'react';
import { css, keyframes, styled } from 'styled-components';

export type SpinnerSize = 'small' | 'medium' | 'large';

export interface SpinnerProps {
	// Accessible name announced by screen readers
	label?: string;
	// Visual size
	size?: SpinnerSize;
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const sizeStyles = {
	small: css`
		width: 16px;
		height: 16px;
		border-width: 2px;
	`,
	medium: css`
		width: 24px;
		height: 24px;
		border-width: 3px;
	`,
	large: css`
		width: 40px;
		height: 40px;
		border-width: 4px;
	`,
} as const;

const SpinnerWrap = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
`;

const Circle = styled.div<{ $size: SpinnerSize }>`
	border-style: solid;
	border-color: var(--Grey-40) var(--Grey-40) var(--Grey-40) transparent;
	border-radius: 50%;
	animation: ${spin} 0.8s linear infinite;
	${(p) => sizeStyles[p.$size]}
`;

// Default accessible label
const DEFAULT_LABEL = 'Loading…';

function SpinnerBase({ label = DEFAULT_LABEL, size = 'medium' }: SpinnerProps) {
	return (
		<SpinnerWrap role="status" aria-live="polite" aria-label={label}>
			<Circle aria-hidden={true} $size={size} />
		</SpinnerWrap>
	);
}

export const Spinner = memo(SpinnerBase);
export default Spinner;
